import { useEffect, useState } from 'react';

import { useApplication } from '@eneco/common/application';
import logger from '@eneco/common/log';
import { host_csr, host, key, site } from '@sitecore/client/config';
import type { PaginationParameter, NumberValue } from '@sitecore/types/manual/PaginatedContent';

import { PagesByTags, PaginatedContentProps } from './types';

const headers: HeadersInit = Object.freeze({
  'content-type': 'application/json',
});

export const indexSearchParam = 'page';

const convertArrayToCSV = (psv: PaginationParameter) => {
  const quotedIds = psv.map(item => `"{${item.id}}"`).join(',');
  return `[${quotedIds}]`;
};

export const usePagination = (tags: PaginationParameter, templates: PaginationParameter, pageSize: NumberValue) => {
  const { locale, searchParams } = useApplication();
  const [index, setIndex] = useState(getCurrentIndex());

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [articles, setArticles] = useState<PagesByTags['articles']>([]);
  const [indexLimit, setIndexLimit] = useState<number | undefined>(undefined);

  const articlesPerPage = pageSize?.value ?? 9;

  function getCurrentIndex() {
    const newIndex = searchParams.get(indexSearchParam);

    //We reduce the index by 1 to make it 0-based,
    //user-facing indexes start at 1
    return newIndex ? +newIndex - 1 : 0;
  }

  useEffect(() => {
    setIndex(getCurrentIndex());
  }, [searchParams]);

  const csvTags = convertArrayToCSV(tags);
  const csvTemplates = convertArrayToCSV(templates);

  useEffect(() => {
    const fetchContent = async () => {
      setIsLoading(true);
      const { responseBody, statusCode } = await fetchPaginatedContent({
        tags: csvTags,
        templates: csvTemplates,
        take: articlesPerPage,
        skip: index * articlesPerPage,
        locale,
      });

      if (statusCode !== 200 || !responseBody) {
        setIsError(true);
      } else {
        const { articles, total } = responseBody.data.pagesbytags;
        setArticles(articles);
        setIndexLimit(Math.ceil(total / articlesPerPage) - 1);
      }
      setIsLoading(false);
    };
    fetchContent();
  }, [csvTags, csvTemplates, index, locale, articlesPerPage]);

  return { isLoading, isError, articles, index, indexLimit, articlesPerPage };
};

/**
 * Request paginated content according to the graphQL query pattern.
 * For more info, see the playground at [sitecore-host]/sitecore/api/graph/ContentServices/ui/
 */
async function fetchPaginatedContent({ locale, tags, templates, take, skip }: PaginatedContentProps) {
  let response;
  //Making a URL fails when the host is undefined,
  //so we try catch it in case env vars are unavailable
  let url;
  try {
    url = new URL('sitecore/api/graph/ContentServices', host ?? host_csr);
    url.search = new URLSearchParams({
      sc_apikey: key,
    }).toString();

    const query = `
  {
    pagesbytags(
      tags: ${tags},
      templates: ${templates},
      take: ${take},
      skip: ${skip},
      language: "${locale}",
      sitename: "${site}"
    ) {
      total
      articles {
        url
        fields {
          title
          pagelinktext
          image {
            src
            alt
            width
            height
          }
        }
      }
    }
  }
`;

    response = await fetch(url.href, {
      headers,
      method: 'POST',
      body: JSON.stringify({
        operationName: null,
        variables: {},
        query,
      }),
    });

    if (!response.ok) throw new Error('Network response was not ok');

    const returnValue = {
      responseBody: (await response.json()) as { data: { pagesbytags: PagesByTags } },
      statusCode: response.status,
    };

    if (!returnValue.responseBody?.data?.pagesbytags?.articles?.length) throw new Error('Unexpected empty response');

    return returnValue;
  } catch (e) {
    logger.error('yBs6X3', 'Error fetching paginated content', {
      error: e,
      req: { tags, templates, take, skip, href: url?.href ?? 'invalid url' },
    });
    return { statusCode: response?.status ?? 500, responseBody: undefined };
  }
}
